// components/CommentItem.tsx
import { Cancel, Edit, Save } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { formatRelativeTime } from '../../../utils';
import './Comment.css';
import type { Comment } from './CommentTypes';

interface CommentItemProps {
  comment: Comment;
  onReply: (commentId: string) => void;
  onEdit: (commentId: string, text: string) => void;
  onDelete: (commentId: string) => void;
  level?: number;
  isDisabled?: boolean;
  loadingStates?: {
    editing?: string;
    deleting?: string;
  };

  onSelect?: (threadId: string) => void;
  threadId?: string;
  isSelected?: boolean;
  hideActions?: boolean;
  showLowerBorder?: boolean;
  showReplyFormating?: boolean;
  flatHeirarchy?: boolean;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onReply,
  onEdit,
  onDelete,
  level = 0,
  isDisabled = false,
  loadingStates = {},

  onSelect,
  threadId,
  isSelected,
  hideActions = false,
  showLowerBorder = false,
  showReplyFormating = false,
  flatHeirarchy = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const user = useSelector((state: RootState) => state.user);
  const isOwner = comment?.user_email === user?.email;

  const commentRef = useRef<HTMLDivElement>(null);

  const isEditingThisComment = loadingStates.editing === comment.id;
  const isDeletingThisComment = loadingStates.deleting === comment.id;
  const isAnyLoading = isDisabled || isEditingThisComment || isDeletingThisComment;

  // Limit nesting depth to prevent UI breaking
  const maxNestingLevel = 4;
  const actualLevel = Math.min(level, maxNestingLevel);
  const leftMargin = actualLevel * 16;

  const handleSaveEdit = () => {
    if (editText.trim() && editText !== comment.text && !isAnyLoading) {
      onEdit(comment.id, editText.trim());
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    if (!isAnyLoading) {
      setEditText(comment.text);
      setIsEditing(false);
    }
  };

  const handleDelete = () => {
    if (!isAnyLoading) {
      onDelete(comment.id);
    }
  };

  const handleClick = () => {
    if (onSelect && threadId) {
      onSelect(threadId);
    }
  };

  return (
    <div
      style={{
        paddingLeft: flatHeirarchy && level > 0 ? 0 : leftMargin,
        marginBottom: 12,
        marginTop: level > 0 ? 12 : 0,
        minWidth: 0,
        width: '100%',
      }}
    >
      <div
        ref={commentRef} // NEW: Add ref to the main comment container
        onClick={handleClick} // NEW: Add click handler
        style={{
          backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
          position: 'relative', // keep so we can position the corner
          minWidth: 180,
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: showLowerBorder ? '12px' : '0px',
          paddingBottom: showLowerBorder ? '12px' : '0px',
          paddingLeft: flatHeirarchy ? '0px' : '12px',
          paddingRight: flatHeirarchy ? '0px' : '12px',
          transition: 'all 0.2s ease',
          overflowX: 'hidden',
          cursor: onSelect ? 'pointer' : 'default',
          border: isSelected ? '2px solid #2196f3' : '1px solid transparent',
          borderBottom: showLowerBorder ? '1px solid #D4DADF' : 'none',
          // remove the straight borderLeft
          marginLeft: level > 0 && showReplyFormating ? '0px' : undefined,
        }}
        onMouseEnter={(e) => {
          const buttons = e.currentTarget.querySelector('.comment-buttons');
          if (buttons) {
            (buttons as HTMLElement).style.opacity = '1';
            (buttons as HTMLElement).style.visibility = 'visible';
          }
          // NEW: Hover effect when clickable
          if (onSelect && !isSelected) {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          const buttons = e.currentTarget.querySelector('.comment-buttons');
          if (buttons) {
            (buttons as HTMLElement).style.opacity = '0';
            (buttons as HTMLElement).style.visibility = 'hidden';
          }
          // NEW: Remove hover effect
          if (onSelect && !isSelected) {
            e.currentTarget.style.backgroundColor = '#ffffff';
          }
        }}
      >
        {level > 0 && showReplyFormating && (
          <div
            style={{
              position: 'absolute',
              left: '0px',
              top: '-12px',
              width: '8px',
              height: '24px',
              borderLeft: '1px solid #ABABAB',
              borderBottom: '1px solid #ABABAB',
              borderRadius: '0 0 0 4px',
            }}
          />
        )}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <img src="images/usercmt.svg" alt="" />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#131426',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {isOwner ? (
                  <span style={{ color: '#131426', marginLeft: 1 }}>You</span>
                ) : (
                  <span style={{ color: '#131426', marginLeft: 1 }}>
                    {comment?.user_email ? comment.user_email.split('@')[0] : 'Unknown'}
                  </span>
                )}
              </span>
              <span style={{ fontSize: 8, color: '#131426', fontFamily: 'Inter, sans-serif' }}>
                {formatRelativeTime(
                  comment?.timestamp || comment?.created_at || comment?.updated_at || ''
                )}
              </span>
              {isDeletingThisComment && <CircularProgress size={12} style={{ color: '#6c757d' }} />}
            </div>
          </div>

          {isEditing ? (
            <div>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                disabled={isEditingThisComment}
                style={{
                  width: '100%',
                  minHeight: 60,
                  padding: '6px 8px',
                  border: '1px solid #1B54F8',
                  borderRadius: 4,
                  fontSize: 12,
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'Inter, sans-serif',
                  backgroundColor: isEditingThisComment ? '#f8f9fa' : 'white',
                  boxSizing: 'border-box',
                }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                <button
                  onClick={handleSaveEdit}
                  disabled={isEditingThisComment || !editText.trim()}
                  style={{
                    padding: '4px 8px',
                    backgroundColor:
                      isEditingThisComment || !editText.trim() ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    fontSize: 11,
                    cursor: isEditingThisComment || !editText.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    whiteSpace: 'nowrap',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {isEditingThisComment ? (
                    <CircularProgress size={12} style={{ color: 'white' }} />
                  ) : (
                    <Save style={{ fontSize: 12 }} />
                  )}
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isEditingThisComment}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    fontSize: 11,
                    cursor: isEditingThisComment ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    whiteSpace: 'nowrap',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  <Cancel style={{ fontSize: 12 }} />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: '#212529',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {comment.text}
            </p>
          )}
        </div>

        {!hideActions && (
          <div
            className="comment-buttons"
            style={{
              display: 'flex',
              gap: 0,
              alignItems: 'center',
              opacity: 0,
              visibility: 'hidden',
              transition: 'all 0.2s ease',
              marginLeft: 12,
              paddingTop: '4px',
              marginTop: '-40px',
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReply(comment.id);
              }}
              disabled={isAnyLoading}
              className="reply-btn"
              style={{
                fontSize: 11,
                background: 'transparent',
                border: 'none',
                cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                padding: '5px 5px',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                whiteSpace: 'nowrap',
                fontFamily: 'Inter, sans-serif',
                borderRadius: 6,
                transition: 'background-color 0.2s ease, filter 0.2s ease',
                marginTop: '8px',
              }}
              onMouseEnter={(e) => {
                if (!isAnyLoading) {
                  e.currentTarget.style.backgroundColor = '#F2F4FE';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                const img = e.currentTarget.querySelector('img') as HTMLImageElement;
                if (img) img.style.filter = 'none'; // reset
              }}
            >
              <img
                src="images/reply-new.svg"
                style={{ width: 10, height: 10, transition: 'filter 0.2s ease' }}
              />
            </button>

            {isOwner && !isEditing && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                  }}
                  className="edit-btn"
                  disabled={isAnyLoading}
                  style={{
                    fontSize: 11,
                    background: 'none',
                    border: 'none',
                    cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                    padding: '5px 5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    whiteSpace: 'nowrap',
                    fontFamily: 'Inter, sans-serif',
                    overflowX: 'hidden',
                    borderRadius: 6,
                    transition: 'background-color 0.2s ease, filter 0.2s ease',
                    marginTop: '8px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isAnyLoading) {
                      e.currentTarget.style.backgroundColor = '#F2F4FE';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    const img = e.currentTarget.querySelector('img') as HTMLImageElement;
                    if (img) img.style.filter = 'none'; // reset
                  }}
                >
                  <Edit
                    sx={{
                      width: 10,
                      height: 10,
                      transition: 'color 0.2s ease',
                      color: '#4D5358',
                      '&:hover': {
                        color: '#1B54F8',
                      },
                    }}
                  />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  className="trash-btn"
                  disabled={isAnyLoading}
                  style={{
                    fontSize: 11,
                    color: isAnyLoading ? '#ffffff' : '#ffffff',
                    background: 'none',
                    border: 'none',
                    cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                    padding: '5px 5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    whiteSpace: 'nowrap',
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: 6,
                    transition: 'background-color 0.2s ease, filter 0.2s ease',
                    marginTop: '8px',
                  }}
                  onMouseEnter={(e) => {
                    if (!isAnyLoading) {
                      e.currentTarget.style.backgroundColor = '#F2F4FE';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    const img = e.currentTarget.querySelector('img') as HTMLImageElement;
                    if (img) img.style.filter = 'none'; // reset
                  }}
                >
                  {isDeletingThisComment ? (
                    <CircularProgress size={12} style={{ color: '#6c757d' }} />
                  ) : (
                    <img
                      src="images/trash-new.svg"
                      style={{ width: 10, height: 10, transition: 'filter 0.2s ease' }}
                    />
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {comment.replies?.map((reply) => (
        <CommentItem
          key={reply.id}
          comment={reply}
          onReply={onReply}
          onEdit={onEdit}
          onDelete={onDelete}
          level={level + 1}
          isDisabled={isDisabled}
          loadingStates={loadingStates}
          onSelect={onSelect}
          threadId={threadId}
          isSelected={isSelected}
          hideActions
          showReplyFormating={showReplyFormating}
          flatHeirarchy={flatHeirarchy}
          showLowerBorder={showLowerBorder}
        />
      ))}
    </div>
  );
};

export default CommentItem;
